/**
 * Donor Pipeline API client (weavein) — Fundraising Suite (epic TRUS-326), Track C.
 *
 * Talks to the WEAVEIN donor-pipeline router (/api/donor-pipeline/*) via authFetch
 * (Authorization + X-CBO-ID). This replaces the earlier use of the platform sales
 * API (/api/platform/sales/*), which is get_platform_user-gated and 401s for
 * weavein staff — that 401 tripped the apiClient logout interceptor and crashed
 * the board.
 */

import { authFetchJson } from '@/lib/authFetch';

export interface DonorStage {
  id: string;
  name: string;
  position: number;
  probability: number;
  color: string;
  isWon?: boolean;
  isLost?: boolean;
}

export interface DonorPipeline {
  id: string;
  name: string;
  stages: DonorStage[];
}

export interface DonorOpportunity {
  id: string;
  name: string;
  company: string;
  amount: number;
  stage: string;
  probability: number;
  closeDate: string;
  owner: string;
  /** The donor record this ask belongs to. Set from the picker — cards used to be
   *  free-typed names with no relationship to any member, so the contact record's
   *  cultivation panel (which filters on this) was always empty. */
  donorMemberId?: number | null;
  donorName?: string | null;
  donorEmail?: string | null;
  donorRecordType?: string | null;
  lifetimeGiving?: number;
}

export interface DonorOpportunityCreate {
  name: string;
  company?: string;
  amount?: number;
  stage?: string;
  closeDate?: string;
  owner?: string;
  donorMemberId?: number | null;
}

export async function getDonorPipeline(): Promise<DonorPipeline> {
  return authFetchJson<DonorPipeline>('/api/donor-pipeline/pipeline');
}

export async function listDonorOpportunities(
  params: { donor_member_id?: number; stage?: string } = {},
): Promise<DonorOpportunity[]> {
  const usp = new URLSearchParams();
  if (params.donor_member_id != null) usp.set('donor_member_id', String(params.donor_member_id));
  if (params.stage) usp.set('stage', params.stage);
  const qs = usp.toString();
  return authFetchJson<DonorOpportunity[]>(`/api/donor-pipeline/opportunities${qs ? `?${qs}` : ''}`);
}

export async function createDonorOpportunity(payload: DonorOpportunityCreate): Promise<DonorOpportunity> {
  return authFetchJson<DonorOpportunity>('/api/donor-pipeline/opportunities', { method: 'POST', jsonBody: payload });
}

export async function moveDonorOpportunity(id: string, stage: string, probability?: number): Promise<DonorOpportunity> {
  return authFetchJson<DonorOpportunity>(`/api/donor-pipeline/opportunities/${id}/stage`, {
    method: 'PUT',
    jsonBody: { stage, probability },
  });
}

export async function updateDonorOpportunity(
  id: string,
  payload: Partial<DonorOpportunityCreate> & { stage?: string; probability?: number },
): Promise<DonorOpportunity> {
  return authFetchJson<DonorOpportunity>(`/api/donor-pipeline/opportunities/${id}`, {
    method: 'PUT',
    jsonBody: payload,
  });
}

export async function deleteDonorOpportunity(id: string): Promise<void> {
  await authFetchJson<void>(`/api/donor-pipeline/opportunities/${id}`, { method: 'DELETE' });
}
